/**
 * Account Health — /glowyai/health-check. Efe only for now (backend is e-mail locked).
 * Latest run of ~70 read-only checks across Google Ads, Meta, GA4 and cross-channel data:
 * score + category rings, issue list by severity, fix-first rail, budget recommendations,
 * Google's own recommendations. Data comes from /api/health-check/latest; nothing here
 * changes the ad accounts.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ShieldCheck, RefreshCw, ChevronDown, ChevronRight, CheckCircle2, CircleSlash, AlertTriangle,
  AlertOctagon, Info, TrendingUp, Wallet, Search, Radio, Target, Image as ImageIcon, Boxes, Send,
  Clock, Layers, ArrowUpRight, Sparkles, Plus, Wrench, Maximize2, Rocket, Activity, Megaphone,
} from 'lucide-react';
import { apiFetch } from '../utils/apiFetch';
import { useAuth } from '../context/AuthContext';

const API = 'https://api.glowytics.com/api/health-check';

const T = {
  bg: '#F6F8FF', card: '#FFFFFF', line: '#E5E7EB', line2: '#F3F4F6', soft: '#F8FAFF',
  ink: '#111827', ink2: '#374151', ink3: '#6B7280', ink4: '#9CA3AF',
  blue: '#436AEB', blueSoft: '#EEF2FE',
  red: '#DC2626', redSoft: '#FEF2F2', amber: '#D97706', amberSoft: '#FFFBEB', green: '#059669', greenSoft: '#ECFDF5',
};
const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const SEV = {
  critical: { name: 'Critical', plural: 'Critical', color: T.red, soft: T.redSoft, icon: AlertOctagon, rank: 0, weight: 0 },
  warning:  { name: 'Warning',  plural: 'Warnings', color: T.amber, soft: T.amberSoft, icon: AlertTriangle, rank: 1, weight: 0.5 },
  info:     { name: 'Notice',   plural: 'Notices', color: T.blue, soft: T.blueSoft, icon: Info, rank: 2, weight: 0.85 },
  positive: { name: 'Positive', plural: 'Positive', color: T.green, soft: T.greenSoft, icon: TrendingUp, rank: 3, weight: 1 },
};
const SEV_ORDER = ['critical', 'warning', 'info', 'positive'];

const CATEGORY = {
  waste:    { name: 'Wasted spend',          icon: Wallet },
  budget:   { name: 'Budget & bidding',      icon: Target },
  search:   { name: 'Search terms',          icon: Search },
  network:  { name: 'Networks & targeting',  icon: Radio },
  tracking: { name: 'Conversion tracking',   icon: ShieldCheck },
  traffic:  { name: 'Traffic quality',       icon: Activity },
  ads:      { name: 'Ads & assets',          icon: ImageIcon },
  pmax:     { name: 'PMax & catalog',        icon: Boxes },
  delivery: { name: 'Delivery & audiences',  icon: Send },
  // Branding kampanyaları satışla değil izlenme / erişim / trafikle ölçülür (backend branding.js)
  branding: { name: 'Branding',              icon: Megaphone },
};
const CATEGORY_ORDER = ['waste', 'budget', 'search', 'network', 'tracking', 'traffic', 'ads', 'pmax', 'delivery', 'branding'];

const PLATFORM = {
  google: { name: 'Google Ads', color: '#1A73E8' },
  meta:   { name: 'Meta',       color: '#0866FF' },
  ga4:    { name: 'GA4',        color: '#E37400' },
  cross:  { name: 'Cross-channel', color: '#7C3AED' },
};

// --- formatting ---------------------------------------------------------------
const num = (v, d = 0) => (v == null || Number.isNaN(Number(v)) ? '–'
  : Number(v).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }));
function money(v) {
  if (v == null) return '–';
  const a = Math.abs(v); const s = v < 0 ? '−' : '';
  if (a >= 1e6) return `${s}₺${num(a / 1e6, 2)}M`;
  if (a >= 1e4) return `${s}₺${num(a / 1e3, 1)}K`;
  return `${s}₺${num(a)}`;
}
const moneyFull = (v) => (v == null ? '–' : `${v < 0 ? '−' : ''}₺${num(Math.abs(v))}`);
const roasX = (v) => (v == null ? '–' : `${num(v, 1)}x`);
const pct = (v, d = 1) => (v == null ? '–' : `${num(v, d)}%`);
const delta = (v) => (v == null ? '–' : v === 0 ? '0%' : `${v > 0 ? '+' : '−'}${num(Math.abs(v))}%`);
const day = (s) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const range = (w) => (w && w[0] ? `${day(w[0])} – ${day(w[1])}` : '');

function cell(v, format) {
  if (v == null || v === '') return '–';
  switch (format) {
    case 'try': return moneyFull(v);
    case 'int': return num(v);
    case 'pct': return pct(v);
    case 'x': return roasX(v);
    default: return String(v);
  }
}

// Passed/positive = 1, notice = 0.85, warning = 0.5, critical = 0; no-data checks don't count.
function healthScore(checks) {
  const counted = checks.filter((c) => c.status !== 'no_data');
  if (!counted.length) return null;
  const total = counted.reduce((t, c) => t + (c.status === 'clean' ? 1 : (SEV[c.severity]?.weight ?? 1)), 0);
  return Math.round((total / counted.length) * 100);
}
const scoreColor = (p) => (p == null ? T.ink4 : p >= 90 ? T.green : p >= 70 ? T.amber : T.red);
const scoreGrade = (p) => (p == null ? 'No data' : p >= 90 ? 'Healthy' : p >= 75 ? 'Good, with fixes' : p >= 50 ? 'Needs attention' : 'At risk');

async function call(path, brandId, opts = {}) {
  const res = await apiFetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId, ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
  return body;
}

// --- building blocks ------------------------------------------------------------
const card = { background: T.card, border: `1px solid ${T.line}`, borderRadius: 12, boxShadow: '0 1px 2px rgba(16,24,40,0.04)' };
const unbutton = { all: 'unset', boxSizing: 'border-box', cursor: 'pointer', fontFamily: FONT };

// Same logo files the sidebar uses, so the page matches the rest of the panel.
function GoogleAdsLogo({ size = 16 }) {
  return <img src="/google_ads-512.webp" alt="" width={size} height={size} style={{ display: 'block', objectFit: 'contain', flexShrink: 0 }} />;
}
function MetaLogo({ size = 16 }) {
  return <img src="/6033716.png" alt="" width={size} height={size} style={{ display: 'block', objectFit: 'contain', flexShrink: 0 }} />;
}
function GA4Logo({ size = 16 }) {
  return <img src="/GA4logoicon.svg" alt="" width={size} height={size} style={{ display: 'block', objectFit: 'contain', flexShrink: 0 }} />;
}
function PlatformMark({ platform, size = 16 }) {
  if (platform === 'google') return <GoogleAdsLogo size={size} />;
  if (platform === 'meta') return <MetaLogo size={size} />;
  if (platform === 'ga4') return <GA4Logo size={size} />;
  return <Layers size={size} color={PLATFORM.cross.color} />;
}

function Ring({ value, size = 64, stroke = 6, children }) {
  const r = (size - stroke) / 2; const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`Score ${value ?? 'no data'}`} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={T.line2} strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={scoreColor(value)} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={`${((value ?? 0) / 100) * c} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      {children}
    </svg>
  );
}

function SevDot({ severity, size = 22 }) {
  const s = SEV[severity] || SEV.info; const Icon = s.icon;
  return (
    <span style={{ width: size, height: size, borderRadius: 6, background: s.soft, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
      <Icon size={size * 0.6} color={s.color} strokeWidth={2.4} />
    </span>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div style={{ display: 'inline-flex', padding: 3, background: '#EBEEF5', borderRadius: 10, gap: 2, flexWrap: 'wrap' }}>
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button key={o.value} type="button" onClick={() => onChange(o.value)} style={{
            ...unbutton, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600,
            background: on ? T.card : 'transparent', color: on ? T.ink : T.ink3, boxShadow: on ? '0 1px 2px rgba(16,24,40,0.10)' : 'none',
          }}>
            {o.icon}{o.label}
            {o.count != null && <span style={{ fontSize: 12, fontWeight: 600, color: on ? T.blue : T.ink4 }}>{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

function Stat({ label, value, sub, tone }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12, color: T.ink3, marginBottom: 4, whiteSpace: 'nowrap' }}>{label}</div>
      <div style={{ fontSize: 19, fontWeight: 700, color: tone || T.ink, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: T.ink4, marginTop: 2, whiteSpace: 'nowrap' }}>{sub}</div>}
    </div>
  );
}

function DataTable({ table }) {
  if (!table || !Array.isArray(table.rows) || !table.rows.length) return null;
  const more = (table.total_rows || table.rows.length) - table.rows.length;
  return (
    <div style={{ overflowX: 'auto', border: `1px solid ${T.line}`, borderRadius: 10, background: T.card }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
        <thead>
          <tr style={{ background: T.soft }}>
            {table.columns.map((c) => (
              <th key={c.key} style={{ textAlign: c.align || 'left', padding: '9px 12px', fontWeight: 600, color: T.ink3, fontSize: 12, whiteSpace: 'nowrap', borderBottom: `1px solid ${T.line}` }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((r, i) => (
            <tr key={i} style={{ borderTop: i ? `1px solid ${T.line2}` : 'none' }}>
              {table.columns.map((c) => (
                <td key={c.key} style={{ textAlign: c.align || 'left', padding: '8px 12px', color: c.align === 'right' ? T.ink : T.ink2, whiteSpace: c.align === 'right' ? 'nowrap' : 'normal', minWidth: c.align === 'right' ? undefined : 140, overflowWrap: 'anywhere' }}>
                  {cell(r[c.key], c.format)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {more > 0 && <div style={{ padding: '8px 12px', fontSize: 12, color: T.ink3, borderTop: `1px solid ${T.line2}` }}>+{num(more)} more rows</div>}
    </div>
  );
}

function ImpactTag({ impact, compact }) {
  if (!impact) return null;
  const waste = impact.type === 'waste';
  // Collapsed rows show only the wasted amount; longer labels appear when the row is opened.
  if (compact && !(waste && impact.amount_try != null)) return null;
  const text = compact ? money(impact.amount_try) : impact.label;
  if (!text) return null;
  return (
    <span style={{
      fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', padding: '2px 8px', borderRadius: 6, fontVariantNumeric: 'tabular-nums',
      color: waste ? '#B91C1C' : impact.type === 'opportunity' ? T.blue : T.ink2,
      background: waste ? T.redSoft : impact.type === 'opportunity' ? T.blueSoft : T.line2,
    }}>{text}</span>
  );
}

function IssueRow({ c, open, onToggle, first }) {
  const s = SEV[c.severity] || SEV.info;
  return (
    <div id={`hc-${c.key}`} style={{ borderTop: first ? 'none' : `1px solid ${T.line2}`, background: open ? T.soft : 'transparent', transition: 'background 0.15s' }}>
      <button type="button" onClick={onToggle} aria-expanded={open} className="hc-row" style={{ ...unbutton, width: '100%', display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 20px' }}>
        <SevDot severity={c.severity} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 10px' }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>{c.title}</span>
            <span style={{ fontSize: 12, color: T.ink4 }}>{CATEGORY[c.category]?.name}</span>
          </span>
          <span style={{
            display: 'block', fontSize: 13, color: T.ink2, lineHeight: 1.5, marginTop: 3,
            ...(open ? {} : { overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }),
          }}>{c.finding}</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, paddingTop: 1 }}>
          <span className="hc-hide-sm"><ImpactTag impact={c.impact} compact /></span>
          <span title={PLATFORM[c.platform]?.name} style={{ display: 'grid', placeItems: 'center' }}><PlatformMark platform={c.platform} /></span>
          <ChevronDown size={16} color={T.ink3} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
        </span>
      </button>
      {open && (
        <div style={{ padding: '0 20px 18px 54px', display: 'grid', gap: 12 }}>
          {c.impact?.label && <div><ImpactTag impact={c.impact} /></div>}
          {c.action && (
            <div style={{ padding: '10px 14px', background: T.card, border: `1px solid ${T.line}`, borderLeft: `3px solid ${s.color}`, borderRadius: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.ink3, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Recommended action</div>
              <div style={{ fontSize: 14, color: T.ink, lineHeight: 1.55 }}>{c.action}</div>
            </div>
          )}
          {c.table && c.table.rows?.length ? <DataTable table={c.table} /> : (
            c.evidence?.length ? (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: T.ink2, lineHeight: 1.7 }}>
                {c.evidence.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            ) : null
          )}
          {(c.why || c.rule) && (
            <div style={{ display: 'grid', gap: 4, fontSize: 12, color: T.ink3, lineHeight: 1.5 }}>
              {c.why && <span><strong style={{ color: T.ink2, fontWeight: 600 }}>Why it matters · </strong>{c.why}</span>}
              {c.rule && <span><strong style={{ color: T.ink2, fontWeight: 600 }}>Rule · </strong>{c.rule}</span>}
            </div>
          )}
          <div style={{ fontSize: 11, color: T.ink4 }}>{PLATFORM[c.platform]?.name} · check {c.key}</div>
        </div>
      )}
    </div>
  );
}

function RailCard({ title, sub, children, action }) {
  return (
    <div style={{ ...card, padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>{title}</span>
        {sub && <span style={{ fontSize: 12, color: T.ink3 }}>{sub}</span>}
        {action && <span style={{ marginLeft: 'auto' }}>{action}</span>}
      </div>
      {children}
    </div>
  );
}

// Change column: diverging bar around the centre, ±30% fills one side; pause fills red fully.
function ChangeBar({ pctValue, paused }) {
  const v = paused ? -100 : (pctValue || 0);
  const w = Math.min(Math.abs(v) / 30, 1) * 50;
  const color = v > 0 ? T.green : v < 0 ? T.red : T.ink4;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
      <div style={{ position: 'relative', width: 96, height: 8, background: T.line2, borderRadius: 999 }}>
        <span style={{ position: 'absolute', left: '50%', top: -2, bottom: -2, width: 1, background: T.ink4 }} />
        {v !== 0 && <span style={{ position: 'absolute', top: 0, bottom: 0, borderRadius: 999, background: color, ...(v > 0 ? { left: '50%', width: `${w}%` } : { right: '50%', width: `${w}%` }) }} />}
      </div>
      <span style={{ width: 52, textAlign: 'right', fontSize: 12, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{paused ? 'Pause' : delta(v)}</span>
    </div>
  );
}

function BudgetPanel({ data }) {
  const [showSame, setShowSame] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  if (!data || !data.rows?.length) return <div style={{ padding: 24, fontSize: 14, color: T.ink3 }}>No budget data for this channel.</div>;
  const active = data.rows.filter((r) => r.active !== false);
  const closed = data.rows.filter((r) => r.active === false);
  const changed = active.filter((r) => r.change_pct !== 0)
    .sort((a, b) => (b.change_pct === -100) - (a.change_pct === -100) || Math.abs(b.change_pct) - Math.abs(a.change_pct) || b.spend_30d - a.spend_30d);
  const same = active.filter((r) => r.change_pct === 0);
  const diff = (data.proposed_daily_total ?? 0) - (data.current_daily_total ?? 0);
  const up = changed.filter((r) => r.change_pct > 0).length;
  const down = changed.filter((r) => r.change_pct < 0 && r.change_pct > -100).length;
  const pause = changed.filter((r) => r.change_pct === -100).length;
  const rows = showSame ? [...changed, ...same] : changed;
  const th = { padding: '10px 12px', fontSize: 12, fontWeight: 600, color: T.ink3, whiteSpace: 'nowrap', borderBottom: `1px solid ${T.line}`, textAlign: 'right' };
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16, padding: '18px 20px', borderBottom: `1px solid ${T.line2}` }}>
        <Stat label="Current daily total" value={moneyFull(data.current_daily_total)} />
        <Stat label="Proposed daily total" value={moneyFull(data.proposed_daily_total)} />
        <Stat label="Difference per day" value={diff === 0 ? '₺0' : `${diff > 0 ? '+' : '−'}${moneyFull(Math.abs(diff))}`} tone={diff > 0 ? T.green : diff < 0 ? T.red : T.ink} />
        <Stat label="Changes" value={`${up} up · ${down} down · ${pause} pause`} />
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left', paddingLeft: 20 }}>Campaign</th>
              <th style={th}>ROAS</th>
              <th style={th}>Spend (30d)</th>
              <th style={th}>Now / day</th>
              <th style={th}>Proposed / day</th>
              <th style={{ ...th, paddingRight: 20 }}>Change</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.campaign}-${i}`} style={{ borderTop: i ? `1px solid ${T.line2}` : 'none' }}>
                <td style={{ padding: '10px 12px 10px 20px', minWidth: 260, overflowWrap: 'anywhere' }}>
                  <div style={{ color: T.ink, fontWeight: 500 }}>{r.campaign}</div>
                  {r.reason && r.change_pct !== 0 && <div style={{ fontSize: 12, color: T.ink3, marginTop: 2 }}>{r.reason}</div>}
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap', color: T.ink2 }}>{roasX(r.roas)}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap', color: T.ink2 }}>{money(r.spend_30d)}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap', color: T.ink2 }}>{moneyFull(r.current_daily)}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 700, color: T.ink }}>{moneyFull(r.proposed_daily)}</td>
                <td style={{ padding: '10px 20px 10px 12px' }}><ChangeBar pctValue={r.change_pct} paused={r.change_pct === -100 || (r.proposed_daily === 0 && r.current_daily > 0)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, padding: '12px 20px', borderTop: `1px solid ${T.line2}` }}>
        {same.length > 0 && (
          <button type="button" onClick={() => setShowSame((v) => !v)} style={{ ...unbutton, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: T.blue }}>
            <ChevronDown size={15} style={{ transform: showSame ? 'rotate(180deg)' : 'none' }} />
            {showSame ? 'Hide' : 'Show'} {same.length} unchanged campaigns
          </button>
        )}
        {closed.length > 0 && (
          <button type="button" onClick={() => setShowClosed((v) => !v)} style={{ ...unbutton, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: T.ink3 }}>
            <ChevronDown size={15} style={{ transform: showClosed ? 'rotate(180deg)' : 'none' }} />
            {closed.length} campaigns ended during the period (excluded from totals)
          </button>
        )}
      </div>
      {showClosed && (
        <div style={{ padding: '0 20px 14px', display: 'grid', gap: 4, fontSize: 13, color: T.ink3, fontVariantNumeric: 'tabular-nums' }}>
          {closed.map((r, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ overflowWrap: 'anywhere' }}>{r.campaign}</span>
              <span style={{ whiteSpace: 'nowrap' }}>{money(r.spend_30d)} · {roasX(r.roas)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// --- growth opportunities ---------------------------------------------------------
const OPP_TYPE = {
  new_campaign: { name: 'New campaign', icon: Plus, color: '#047857', soft: T.greenSoft },
  expand:       { name: 'Expand',       icon: Maximize2, color: T.blue, soft: T.blueSoft },
  improve:      { name: 'Improve',      icon: Wrench, color: '#B45309', soft: T.amberSoft },
};
// GA4 view also lists opportunities found in GA4 data that are acted on in an ad account (source: 'ga4').
const oppIn = (o, p) => p === 'all' || o.platform === p || (p === 'ga4' && o.source === 'ga4');
const PRIORITY = { high: { name: 'High priority', rank: 0, color: T.red }, medium: { name: 'Medium', rank: 1, color: T.amber }, low: { name: 'Low', rank: 2, color: T.ink4 } };

function demandValue(d) {
  if (d.format === 'try') return money(d.value);
  if (d.format === 'pct') return pct(d.value);
  return num(d.value);
}

function OpportunityCard({ o }) {
  const [open, setOpen] = useState(false);
  const t = OPP_TYPE[o.type] || OPP_TYPE.expand; const TIcon = t.icon;
  const pr = PRIORITY[o.priority] || PRIORITY.medium;
  const p = o.proposal || {};
  const groups = Array.isArray(p.structure) ? p.structure : [];
  const shownGroups = open ? groups : groups.slice(0, 3);
  // Keyword Planner returns close variants with the same volume; show such a figure once.
  const demand = (Array.isArray(o.demand) ? o.demand : []).filter((d, i, arr) => !(d.format === 'int'
    && arr.slice(0, i).some((x) => x.format === 'int' && x.value === d.value && /search/i.test(x.label) && /search/i.test(d.label))));
  return (
    <article style={{ ...card, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
      <div style={{ padding: '16px 18px 0', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: t.color, background: t.soft, padding: '3px 9px', borderRadius: 999 }}>
          <TIcon size={12} strokeWidth={2.6} />{t.name}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: T.ink3 }}>
          <span style={{ width: 7, height: 7, borderRadius: 999, background: pr.color }} />{pr.name}
        </span>
        <span style={{ marginLeft: 'auto' }} title={PLATFORM[o.platform]?.name}><PlatformMark platform={o.platform} /></span>
      </div>
      <div style={{ padding: '10px 18px 0' }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.ink, letterSpacing: '-0.01em' }}>{o.title}</h3>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: T.ink2, lineHeight: 1.55 }}>{o.finding}</p>
      </div>
      {demand.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(demand.length, 3)}, minmax(0, 1fr))`, gap: 12, margin: '14px 18px 0', padding: '12px 14px', background: T.soft, borderRadius: 10 }}>
          {demand.slice(0, 3).map((d, i) => (
            <div key={i} style={{ minWidth: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: T.ink, fontVariantNumeric: 'tabular-nums' }}>{demandValue(d)}</div>
              <div style={{ fontSize: 11, color: T.ink3, marginTop: 2, lineHeight: 1.35 }}>{d.label}</div>
            </div>
          ))}
        </div>
      )}
      {(p.campaign_type || p.name) && (
        <div style={{ margin: '14px 18px 0', border: `1px solid ${T.line}`, borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '10px 12px', background: T.card, borderBottom: groups.length || p.targeting || p.budget_daily_suggestion != null ? `1px solid ${T.line2}` : 'none' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.ink3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Proposed</span>
            {p.campaign_type && <span style={{ fontSize: 12, fontWeight: 700, color: T.blue, background: T.blueSoft, padding: '2px 8px', borderRadius: 6 }}>{p.campaign_type}</span>}
            {p.name && <span style={{ fontSize: 13, fontWeight: 600, color: T.ink, overflowWrap: 'anywhere' }}>{p.name}</span>}
            {p.budget_daily_suggestion != null && (
              <span title={p.budget_basis || ''} style={{ marginLeft: 'auto', fontSize: 12, color: T.ink2, whiteSpace: 'nowrap' }}>
                Start at <strong style={{ color: T.ink }}>{moneyFull(p.budget_daily_suggestion)}</strong>/day
              </span>
            )}
          </div>
          {p.targeting && <div style={{ padding: '8px 12px', fontSize: 12, color: T.ink2, borderBottom: groups.length ? `1px solid ${T.line2}` : 'none', lineHeight: 1.5 }}>{p.targeting}</div>}
          {groups.length > 0 && (
            <div style={{ display: 'grid' }}>
              {shownGroups.map((g, i) => (
                <div key={i} style={{ padding: '9px 12px', borderTop: i ? `1px solid ${T.line2}` : 'none', display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
                    {g.ad_group}{g.match && <span style={{ fontSize: 11, fontWeight: 500, color: T.ink4, marginLeft: 6 }}>{g.match} match</span>}
                  </div>
                  {Array.isArray(g.keywords) && g.keywords.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {g.keywords.map((k) => (
                        <span key={k} style={{ fontSize: 12, color: T.ink2, background: T.line2, padding: '2px 8px', borderRadius: 6 }}>{k}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {groups.length > 3 && (
                <button type="button" onClick={() => setOpen((v) => !v)} style={{ ...unbutton, padding: '8px 12px', borderTop: `1px solid ${T.line2}`, fontSize: 12, fontWeight: 600, color: T.blue }}>
                  {open ? 'Show fewer ad groups' : `Show all ${groups.length} ad groups`}
                </button>
              )}
            </div>
          )}
        </div>
      )}
      <div style={{ marginTop: 'auto', padding: '12px 18px 16px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 10, minWidth: 0 }}>
        {o.table?.rows?.length > 0 && (
          <details>
            <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600, color: T.blue, marginTop: 4 }}>View supporting data</summary>
            <div style={{ marginTop: 10 }}><DataTable table={o.table} /></div>
          </details>
        )}
        {(o.why || o.confidence) && (
          <div style={{ fontSize: 12, color: T.ink3, lineHeight: 1.5 }}>
            {o.why}{o.confidence && <span style={{ color: T.ink4 }}>{o.why ? ' · ' : ''}{o.confidence} confidence</span>}
          </div>
        )}
      </div>
    </article>
  );
}

function Opportunities({ items, platform, scanning }) {
  const [type, setType] = useState('all');
  const scoped = items.filter((o) => oppIn(o, platform));
  const list = scoped.filter((o) => type === 'all' || o.type === type)
    .sort((a, b) => ((PRIORITY[a.priority]?.rank ?? 9) - (PRIORITY[b.priority]?.rank ?? 9)));
  const count = (k) => scoped.filter((o) => o.type === k).length;
  if (!items.length) {
    return (
      <div style={{ ...card, padding: '48px 24px', textAlign: 'center' }}>
        <Rocket size={36} color={T.blue} />
        <div style={{ fontSize: 17, fontWeight: 700, marginTop: 12 }}>{scanning ? 'Looking for opportunities' : 'No opportunities in this scan yet'}</div>
        <div style={{ fontSize: 14, color: T.ink3, marginTop: 6 }}>{scanning ? 'The running scan will add them.' : 'Run a new scan to find campaigns you could launch or grow.'}</div>
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <section style={{ ...card, display: 'flex', flexWrap: 'wrap' }}>
        {[
          { k: 'all', label: 'All opportunities', n: scoped.length, color: T.ink },
          { k: 'new_campaign', label: 'New campaigns', n: count('new_campaign'), color: OPP_TYPE.new_campaign.color },
          { k: 'expand', label: 'Expand coverage', n: count('expand'), color: OPP_TYPE.expand.color },
          { k: 'improve', label: 'Improve what runs', n: count('improve'), color: OPP_TYPE.improve.color },
        ].map((x, i) => {
          const on = type === x.k;
          return (
            <button key={x.k} type="button" onClick={() => setType(x.k)} className="hc-row" style={{
              ...unbutton, flex: '1 1 140px', padding: '16px 20px', borderLeft: i ? `1px solid ${T.line2}` : 'none',
              background: on ? T.blueSoft : 'transparent', boxShadow: on ? `inset 0 -2px 0 ${T.blue}` : 'none',
            }}>
              <div style={{ fontSize: 26, fontWeight: 700, color: x.color, fontVariantNumeric: 'tabular-nums' }}>{x.n}</div>
              <div style={{ fontSize: 13, color: T.ink2, marginTop: 2 }}>{x.label}</div>
            </button>
          );
        })}
      </section>
      <div style={{ fontSize: 13, color: T.ink3, lineHeight: 1.5 }}>
        Campaigns you could launch, coverage you're missing and things worth improving. Demand figures come from Keyword Planner, Search Console, GA4 (site search, carts, traffic), store sales and stock, and the ad accounts; nothing is created automatically.
      </div>
      {list.length ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 420px), 1fr))', gap: 16, alignItems: 'start' }}>
          {/* Several opportunities share a check key (e.g. one O2 per brand), so the title makes the React key unique. */}
          {list.map((o) => <OpportunityCard key={`${o.key}:${o.title}`} o={o} />)}
        </div>
      ) : <div style={{ ...card, padding: 32, textAlign: 'center', fontSize: 14, color: T.ink3 }}>No opportunities in this view.</div>}
    </div>
  );
}

// --- page --------------------------------------------------------------------------
export default function HealthCheck() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id;
  const [run, setRun] = useState(null);
  const [running, setRunning] = useState(false);
  const [canRun, setCanRun] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastError, setLastError] = useState(null);
  const [logo, setLogo] = useState('');
  const [platform, setPlatform] = useState('all');
  const [category, setCategory] = useState('all');
  const [open, setOpen] = useState({});
  const [showPassed, setShowPassed] = useState(false);
  const [budgetTab, setBudgetTab] = useState('google');
  const [tab, setTab] = useState('health');
  const pollRef = useRef(null);

  const load = useCallback(async (quiet) => {
    if (!brandId) return;
    if (!quiet) setLoading(true);
    try {
      const d = await call('/latest', brandId);
      setRun(d.run || null);
      setRunning(!!d.running);
      setCanRun(!!d.can_run);
      setLastError(d.last_error || null);
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [brandId]);

  useEffect(() => { setRun(null); setOpen({}); load(); }, [load]);

  // Poll every 8 s while a scan is running.
  useEffect(() => {
    clearInterval(pollRef.current);
    if (running) pollRef.current = setInterval(() => load(true), 8000);
    return () => clearInterval(pollRef.current);
  }, [running, load]);

  useEffect(() => {
    if (!brandId) return;
    fetch(`https://api.glowytics.com/api/brand-info/${brandId}/details`, { credentials: 'include', headers: { 'X-Brand-ID': brandId } })
      .then((r) => (r.ok ? r.json() : null)).then((d) => setLogo(d?.logoUrl || '')).catch(() => {});
  }, [brandId]);

  const rescan = async () => {
    try {
      await call('/run', brandId, { method: 'POST', body: JSON.stringify({}) });
      setRunning(true);
    } catch (e) {
      setError(e.message);
    }
  };

  const checks = useMemo(() => run?.checks || [], [run]);
  const inPlatform = useCallback((c) => platform === 'all' || c.platform === platform, [platform]);
  const scoped = useMemo(() => checks.filter(inPlatform), [checks, inPlatform]);
  const findings = useMemo(() => scoped.filter((c) => c.status === 'finding'), [scoped]);
  const passed = useMemo(() => scoped.filter((c) => c.status !== 'finding'), [scoped]);
  const score = useMemo(() => healthScore(scoped), [scoped]);

  const sevCount = useMemo(() => {
    const s = { critical: 0, warning: 0, info: 0, positive: 0 };
    findings.forEach((c) => { if (s[c.severity] != null) s[c.severity] += 1; });
    return s;
  }, [findings]);

  const categories = useMemo(() => CATEGORY_ORDER.map((k) => {
    const list = scoped.filter((c) => c.category === k);
    return { k, total: list.length, issues: list.filter((c) => c.status === 'finding' && c.severity !== 'positive').length, score: healthScore(list) };
  }).filter((x) => x.total > 0), [scoped]);

  const listed = useMemo(() => findings
    .filter((c) => category === 'all' || c.category === category)
    .sort((a, b) => ((SEV[a.severity]?.rank ?? 9) - (SEV[b.severity]?.rank ?? 9))
      || ((b.impact?.type === 'waste' ? b.impact.amount_try || 0 : 0) - (a.impact?.type === 'waste' ? a.impact.amount_try || 0 : 0))), [findings, category]);

  const fixFirst = useMemo(() => findings
    .filter((c) => c.severity === 'critical' || c.severity === 'warning')
    .sort((a, b) => (SEV[a.severity].rank - SEV[b.severity].rank) || ((b.impact?.amount_try || 0) - (a.impact?.amount_try || 0)))
    .slice(0, 5), [findings]);

  // Waste items can overlap (a search term sits inside a campaign), so they're shown side by side, never summed.
  const waste = useMemo(() => findings.filter((c) => c.impact?.type === 'waste' && c.impact.amount_try > 0)
    .sort((a, b) => b.impact.amount_try - a.impact.amount_try), [findings]);
  const wasteMax = waste.length ? waste[0].impact.amount_try : 0;

  const googleRecs = useMemo(() => {
    const m = new Map();
    (run?.google_says?.items || []).forEach((it) => {
      const key = `${it.label}|${it.conflicts_with || ''}`;
      const g = m.get(key) || { key, label: it.label, campaigns: [], conflict: it.conflicts_with || null };
      if (it.campaign && !g.campaigns.includes(it.campaign)) g.campaigns.push(it.campaign);
      m.set(key, g);
    });
    return [...m.values()].sort((a, b) => (b.conflict ? 1 : 0) - (a.conflict ? 1 : 0) || b.campaigns.length - a.campaigns.length);
  }, [run]);
  const conflicts = googleRecs.filter((g) => g.conflict);

  const jumpTo = (key) => {
    const c = checks.find((x) => x.key === key);
    if (!c) return;
    setPlatform('all'); setCategory('all');
    if (c.status !== 'finding') setShowPassed(true);
    else setOpen((o) => ({ ...o, [key]: true }));
    setTimeout(() => document.getElementById(`hc-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
  };

  const s = run?.summary || {};
  const scannedAt = run?.ran_at ? new Date(run.ran_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
  const platformCount = (p) => checks.filter((c) => c.status === 'finding' && c.severity !== 'positive' && (p === 'all' || c.platform === p)).length;
  const budgetData = budgetTab === 'google' ? run?.budget?.google : run?.budget?.meta;
  const opps = useMemo(() => run?.opportunities || [], [run]);
  const tabCount = (p) => (tab === 'opps' ? opps.filter((o) => oppIn(o, p)).length : platformCount(p));

  return (
    <div style={{ background: T.bg, minHeight: '100%', padding: '24px clamp(16px, 2.5vw, 32px) 56px', fontFamily: FONT, color: T.ink, boxSizing: 'border-box' }}>
      <style>{`
        @keyframes hc-spin { to { transform: rotate(360deg); } }
        .hc-row:hover { background: ${T.soft}; }
        @media (max-width: 640px) { .hc-hide-sm { display: none; } }
      `}</style>
      <div style={{ maxWidth: 1480, margin: '0 auto', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 20 }}>

        {/* Header */}
        <header style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, border: `1px solid ${T.line}`, display: 'grid', placeItems: 'center', overflow: 'hidden', background: T.card, flexShrink: 0 }}>
            {logo ? <img src={logo} alt="" style={{ maxWidth: '78%', maxHeight: '78%', objectFit: 'contain' }} /> : <ShieldCheck size={24} color={T.blue} />}
          </div>
          <div style={{ minWidth: 0, flex: '1 1 300px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em' }}>Account Health</h1>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: T.blue, background: T.blueSoft, padding: '3px 8px', borderRadius: 6 }}>
                <Sparkles size={12} /> GlowyAI audit
              </span>
            </div>
            <div style={{ fontSize: 13, color: T.ink3, marginTop: 4 }}>
              {selectedBrand?.name}
              {run ? ` · ${checks.length} checks across Google Ads, Meta and GA4 · decision window ${range(run.windows?.decision)}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {scannedAt && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: T.ink3 }}>
                <Clock size={14} /> Last scan {scannedAt}
              </span>
            )}
            {canRun && <button type="button" onClick={rescan} disabled={running || !brandId} style={{
              ...unbutton, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 10,
              background: running ? T.line2 : T.blue, color: running ? T.ink3 : '#fff', fontSize: 14, fontWeight: 600, cursor: running ? 'default' : 'pointer',
            }}>
              <RefreshCw size={15} style={running ? { animation: 'hc-spin 1s linear infinite' } : undefined} />
              {running ? 'Scanning…' : 'Run scan'}
            </button>}
          </div>
        </header>

        {error && <div style={{ ...card, padding: 14, color: '#B91C1C', background: T.redSoft, borderColor: '#FECACA', fontSize: 14 }}>{error}</div>}
        {!running && lastError && (!run?.ran_at || new Date(lastError.at) > new Date(run.ran_at)) && (
          <div style={{ ...card, padding: '12px 16px', background: T.amberSoft, borderColor: '#FDE68A', color: '#92400E', fontSize: 14 }}>
            The last scan didn't finish: {lastError.message}.{run ? ' Showing the previous scan below.' : ''}
          </div>
        )}
        {running && run && (
          <div style={{ ...card, padding: '12px 16px', background: T.blueSoft, borderColor: '#C7D2FE', color: '#3730A3', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <RefreshCw size={14} style={{ animation: 'hc-spin 1s linear infinite' }} /> A new scan is running (about 3–4 minutes). Results below are from the previous scan.
          </div>
        )}

        {loading && !run && <div style={{ ...card, padding: 56, textAlign: 'center', color: T.ink3, fontSize: 14 }}>Loading…</div>}

        {!loading && !run && !error && (
          <div style={{ ...card, padding: '56px 24px', textAlign: 'center' }}>
            <ShieldCheck size={40} color={T.blue} />
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 14 }}>{running ? 'First scan in progress' : 'No scan yet for this brand'}</div>
            <div style={{ fontSize: 14, color: T.ink3, marginTop: 6, maxWidth: 480, marginInline: 'auto', lineHeight: 1.5 }}>
              {running ? 'Reading Google Ads, Meta and GA4. This takes about 3–4 minutes.' : 'Run a scan to check Google Ads, Meta and GA4. The scan is read-only and never changes your accounts.'}
            </div>
          </div>
        )}

        {run && (
          <>
            {/* Page tabs */}
            <nav style={{ display: 'flex', gap: 28, borderBottom: `1px solid ${T.line}`, overflowX: 'auto' }}>
              {[
                { value: 'health', label: 'Health check', icon: <ShieldCheck size={16} />, count: platformCount('all') },
                { value: 'opps', label: 'Growth opportunities', icon: <Rocket size={16} />, count: opps.length },
              ].map((t) => {
                const on = tab === t.value;
                return (
                  <button key={t.value} type="button" onClick={() => setTab(t.value)} style={{
                    ...unbutton, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 2px 12px', marginBottom: -1, whiteSpace: 'nowrap',
                    fontSize: 15, fontWeight: 600, color: on ? T.ink : T.ink3, borderBottom: `2px solid ${on ? T.blue : 'transparent'}`,
                  }}>
                    <span style={{ color: on ? T.blue : T.ink4, display: 'grid' }}>{t.icon}</span>{t.label}
                    <span style={{ fontSize: 12, fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: on ? T.blueSoft : T.line2, color: on ? T.blue : T.ink3 }}>{t.count}</span>
                  </button>
                );
              })}
            </nav>

            {/* Platform switch */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
              <Segmented value={platform} onChange={(v) => { setPlatform(v); setCategory('all'); }} options={[
                { value: 'all', label: 'All channels', count: tabCount('all') },
                { value: 'google', label: 'Google Ads', icon: <GoogleAdsLogo size={14} />, count: tabCount('google') },
                { value: 'meta', label: 'Meta', icon: <MetaLogo size={14} />, count: tabCount('meta') },
                { value: 'ga4', label: 'GA4', icon: <GA4Logo size={14} />, count: tabCount('ga4') },
                { value: 'cross', label: 'Cross-channel', icon: <Layers size={14} color={PLATFORM.cross.color} />, count: tabCount('cross') },
              ]} />
            </div>

            {tab === 'opps' ? <Opportunities items={opps} platform={platform} scanning={running} /> : (<>

            {/* Score + category rings */}
            <section style={{ ...card, display: 'flex', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 400px', minWidth: 0, padding: 24, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 22, borderRight: `1px solid ${T.line2}` }}>
                <Ring value={score} size={136} stroke={11}>
                  <text x="68" y="70" textAnchor="middle" fontSize="38" fontWeight="700" fill={T.ink} fontFamily={FONT}>{score ?? '–'}</text>
                  <text x="68" y="92" textAnchor="middle" fontSize="12" fill={T.ink3} fontFamily={FONT}>of 100</text>
                </Ring>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.ink3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Overall health</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: scoreColor(score), margin: '4px 0 12px' }}>{scoreGrade(score)}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '6px 18px', fontSize: 13, color: T.ink2, fontVariantNumeric: 'tabular-nums' }}>
                    {[['critical', sevCount.critical], ['warning', sevCount.warning], ['info', sevCount.info]].map(([k, n]) => (
                      <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 999, background: SEV[k].color }} /><strong style={{ color: T.ink }}>{n}</strong> {SEV[k].plural.toLowerCase()}
                      </span>
                    ))}
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 999, background: T.green }} /><strong style={{ color: T.ink }}>{passed.filter((c) => c.status === 'clean').length + sevCount.positive}</strong> passed
                    </span>
                  </div>
                </div>
              </div>
              <div style={{ flex: '3 1 560px', padding: '18px 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 4, alignContent: 'center' }}>
                {categories.map(({ k, total, issues, score: cs }) => {
                  const C = CATEGORY[k]; const on = category === k;
                  return (
                    <button key={k} type="button" onClick={() => setCategory(on ? 'all' : k)} className="hc-row" style={{
                      ...unbutton, display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10,
                      background: on ? T.blueSoft : 'transparent', outline: on ? `1.5px solid ${T.blue}` : 'none',
                    }}>
                      <Ring value={cs} size={48} stroke={5}>
                        <text x="24" y="28.5" textAnchor="middle" fontSize="13" fontWeight="700" fill={T.ink} fontFamily={FONT}>{cs ?? '–'}</text>
                      </Ring>
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.ink, lineHeight: 1.3 }}>{C.name}</span>
                        <span style={{ display: 'block', fontSize: 12, color: issues ? T.ink3 : T.green, marginTop: 2 }}>{issues ? `${issues} of ${total} flagged` : `All ${total} passed`}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Channel stat strip */}
            <section style={{ ...card, overflow: 'hidden' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {s.google && (platform === 'all' || platform === 'google') && (
                  <div style={{ flex: '1 1 380px', padding: '18px 22px', borderRight: `1px solid ${T.line2}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 13, fontWeight: 700 }}><GoogleAdsLogo /> Google Ads</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(88px, 1fr))', gap: '14px 16px' }}>
                      <Stat label="Spend" value={money(s.google.spend)} />
                      {s.google.branding_spend > 0
                        ? <Stat label="Branding" value={money(s.google.branding_spend)} sub="not judged on ROAS" />
                        : <Stat label="Conv. value" value={money(s.google.value)} />}
                      <Stat label={s.google.branding_spend > 0 ? 'Performance ROAS' : 'ROAS'} value={roasX(s.google.performance && s.google.branding_spend > 0 ? s.google.performance.roas : s.google.roas)} />
                      <Stat label="Non-brand ROAS" value={roasX(s.google.roas_nonbrand)} />
                    </div>
                  </div>
                )}
                {s.meta && (platform === 'all' || platform === 'meta') && (
                  <div style={{ flex: '1 1 380px', padding: '18px 22px', borderRight: `1px solid ${T.line2}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 13, fontWeight: 700 }}><MetaLogo /> Meta</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(88px, 1fr))', gap: '14px 16px' }}>
                      <Stat label="Spend" value={money(s.meta.spend)} />
                      {s.meta.branding_spend > 0
                        ? <Stat label="Branding" value={money(s.meta.branding_spend)} sub="not judged on ROAS" />
                        : <Stat label="Conv. value" value={money(s.meta.value)} />}
                      <Stat label={s.meta.branding_spend > 0 ? 'Performance ROAS' : 'ROAS'} value={roasX(s.meta.performance && s.meta.branding_spend > 0 ? s.meta.performance.roas : s.meta.roas)} />
                      <Stat label="Cost / purchase" value={moneyFull(s.meta.performance && s.meta.branding_spend > 0 ? s.meta.performance.cpa : s.meta.cpa)} />
                    </div>
                  </div>
                )}
                {s.traffic && (platform === 'all' || platform === 'ga4') && (
                  <div style={{ flex: '1 1 380px', padding: '18px 22px', borderRight: `1px solid ${T.line2}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 13, fontWeight: 700 }}><GA4Logo /> GA4 traffic</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(88px, 1fr))', gap: '14px 16px' }}>
                      <Stat label="Sessions" value={num(s.traffic.sessions)} />
                      <Stat label="Engaged" value={pct(s.traffic.engaged_rate)} />
                      <Stat label="Revenue / session" value={moneyFull(s.traffic.revenue_per_session)} />
                      <Stat label="Paid share" value={pct(s.traffic.paid_share)} sub="of sessions" />
                    </div>
                  </div>
                )}
                {(s.ikas || s.ga4) && (platform === 'all' || platform === 'cross') && (
                  <div style={{ flex: '1 1 300px', padding: '18px 22px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 13, fontWeight: 700 }}><Layers size={16} color={PLATFORM.cross.color} /> Store & measurement</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(88px, 1fr))', gap: '14px 16px' }}>
                      {s.ikas && <Stat label="Online revenue" value={money(s.ikas.online_revenue)} />}
                      {s.ikas && <Stat label="Orders" value={num(s.ikas.online_orders)} />}
                      {s.ga4 && <Stat label="GA4 coverage" value={s.ikas?.online_orders ? pct((s.ga4.transactions / s.ikas.online_orders) * 100) : num(s.ga4.transactions)} sub={s.ikas?.online_orders ? 'of store orders' : 'transactions'} />}
                    </div>
                  </div>
                )}
              </div>
              {s.lag && (platform === 'all' || platform === 'google') && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 22px', background: T.amberSoft, borderTop: '1px solid #FDE68A', fontSize: 13, color: '#78350F', lineHeight: 1.55 }}>
                  <Clock size={15} color={T.amber} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span>
                    <strong>Attribution lag.</strong> Google conversion value is {money(s.lag.google_db_value)} in Glowytics but {money(s.lag.google_live_value)} in Google Ads ({roasX(s.lag.ratio)}), because late conversions are credited back to the click date. Google figures on this page come straight from Google Ads, and the last 3 days ({range(run.windows?.early)}) are left out of decisions.
                  </span>
                </div>
              )}
            </section>

            {/* Issues + rail */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
              <section style={{ ...card, flex: '1 1 640px', minWidth: 0, overflow: 'hidden' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '16px 20px', borderBottom: `1px solid ${T.line}` }}>
                  <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Issues</h2>
                  <span style={{ fontSize: 13, color: T.ink3 }}>{listed.length} found{category !== 'all' ? ` in ${CATEGORY[category].name}` : ''}</span>
                  {category !== 'all' && (
                    <button type="button" onClick={() => setCategory('all')} style={{ ...unbutton, fontSize: 13, fontWeight: 600, color: T.blue }}>Clear filter</button>
                  )}
                  <button type="button" onClick={() => {
                    const all = listed.every((c) => open[c.key]);
                    setOpen(Object.fromEntries(listed.map((c) => [c.key, !all])));
                  }} style={{ ...unbutton, marginLeft: 'auto', fontSize: 13, color: T.ink3 }}>
                    {listed.length && listed.every((c) => open[c.key]) ? 'Collapse all' : 'Expand all'}
                  </button>
                </div>
                {SEV_ORDER.map((sev) => {
                  const group = listed.filter((c) => c.severity === sev);
                  if (!group.length) return null;
                  return (
                    <div key={sev}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 20px', background: T.soft, borderBottom: `1px solid ${T.line2}`, borderTop: `1px solid ${T.line2}`, fontSize: 12, fontWeight: 700, color: SEV[sev].color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        <span style={{ width: 8, height: 8, borderRadius: 999, background: SEV[sev].color }} />{SEV[sev].plural}
                        <span style={{ color: T.ink4, fontWeight: 600 }}>{group.length}</span>
                      </div>
                      {group.map((c, i) => (
                        <IssueRow key={c.key} c={c} first={i === 0} open={!!open[c.key]} onToggle={() => setOpen((o) => ({ ...o, [c.key]: !o[c.key] }))} />
                      ))}
                    </div>
                  );
                })}
                {!listed.length && <div style={{ padding: 40, textAlign: 'center', fontSize: 14, color: T.ink3 }}>No issues in this view.</div>}

                {/* Passed checks */}
                <div style={{ borderTop: `1px solid ${T.line}` }}>
                  <button type="button" onClick={() => setShowPassed((v) => !v)} className="hc-row" style={{ ...unbutton, width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '14px 20px', fontSize: 14, fontWeight: 600, color: T.ink2 }}>
                    <CheckCircle2 size={16} color={T.green} />
                    Passed checks
                    <span style={{ fontWeight: 600, color: T.ink4 }}>{passed.length}</span>
                    <ChevronDown size={16} color={T.ink3} style={{ marginLeft: 'auto', transform: showPassed ? 'rotate(180deg)' : 'none' }} />
                  </button>
                  {showPassed && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))', gap: '2px 24px', padding: '0 20px 16px' }}>
                      {passed.map((c) => (
                        <div key={c.key} id={`hc-${c.key}`} style={{ display: 'flex', gap: 10, padding: '8px 0', borderTop: `1px solid ${T.line2}` }}>
                          {c.status === 'clean' ? <CheckCircle2 size={16} color={T.green} style={{ flexShrink: 0, marginTop: 2 }} /> : <CircleSlash size={16} color={T.ink4} style={{ flexShrink: 0, marginTop: 2 }} />}
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: T.ink }}>
                              {c.title} <PlatformMark platform={c.platform} size={12} />
                            </div>
                            <div style={{ fontSize: 12, color: T.ink3, marginTop: 2, lineHeight: 1.45 }}>{c.finding || (c.status === 'clean' ? 'No issues found.' : 'No data.')}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>

              <aside style={{ flex: '0 1 380px', minWidth: 0, display: 'grid', gap: 16, position: 'sticky', top: 16 }}>
                <RailCard title="Fix first" sub="highest impact">
                  {fixFirst.length ? fixFirst.map((c, i) => (
                    <button key={c.key} type="button" onClick={() => jumpTo(c.key)} className="hc-row" style={{ ...unbutton, width: 'calc(100% + 16px)', display: 'flex', gap: 10, alignItems: 'flex-start', padding: '9px 8px', margin: '0 -8px', borderRadius: 8 }}>
                      <span style={{ width: 22, height: 22, borderRadius: 999, flexShrink: 0, display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700, color: '#fff', background: SEV[c.severity].color }}>{i + 1}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.ink, lineHeight: 1.35 }}>{c.title}</span>
                        <span style={{ display: 'block', fontSize: 12, color: T.ink3, marginTop: 2, lineHeight: 1.45 }}>{c.action}</span>
                      </span>
                      <ArrowUpRight size={14} color={T.ink4} style={{ flexShrink: 0, marginTop: 3 }} />
                    </button>
                  )) : <div style={{ fontSize: 13, color: T.ink3 }}>Nothing critical or urgent.</div>}
                </RailCard>

                {waste.length > 0 && (
                  <RailCard title="Spend without return" sub="decision window">
                    <div style={{ display: 'grid', gap: 12 }}>
                      {waste.map((c) => (
                        <button key={c.key} type="button" onClick={() => jumpTo(c.key)} style={{ ...unbutton, display: 'grid', gap: 5 }}>
                          <span style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13 }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: T.ink2, minWidth: 0 }}><PlatformMark platform={c.platform} size={12} />{c.title}</span>
                            <strong style={{ color: '#B91C1C', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{moneyFull(c.impact.amount_try)}</strong>
                          </span>
                          <span style={{ height: 6, borderRadius: 999, background: T.line2, overflow: 'hidden' }}>
                            <span style={{ display: 'block', height: '100%', width: `${(c.impact.amount_try / wasteMax) * 100}%`, background: '#F87171', borderRadius: 999 }} />
                          </span>
                        </button>
                      ))}
                    </div>
                    <div style={{ fontSize: 12, color: T.ink4, marginTop: 12, lineHeight: 1.45 }}>Items can overlap (a search term sits inside a campaign), so they aren't added up.</div>
                  </RailCard>
                )}

                {run.google_says?.items?.length > 0 && (platform === 'all' || platform === 'google') && (
                  <RailCard title="Google's recommendations" action={
                    <button type="button" onClick={() => document.getElementById('hc-google-recs')?.scrollIntoView({ behavior: 'smooth' })} style={{ ...unbutton, fontSize: 12, fontWeight: 600, color: T.blue, display: 'inline-flex', alignItems: 'center', gap: 2 }}>View all <ChevronRight size={14} /></button>
                  }>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <Ring value={run.google_says.optimization_score} size={56} stroke={6}>
                        <text x="28" y="32.5" textAnchor="middle" fontSize="13" fontWeight="700" fill={T.ink} fontFamily={FONT}>{run.google_says.optimization_score != null ? Math.round(run.google_says.optimization_score) : '–'}</text>
                      </Ring>
                      <div style={{ fontSize: 13, color: T.ink2, lineHeight: 1.5 }}>
                        {run.google_says.optimization_score != null ? `Optimization score ${pct(run.google_says.optimization_score)} with ` : ''}{run.google_says.items.length} open recommendations.
                        {conflicts.length > 0 && <> <strong style={{ color: '#B91C1C' }}>{conflicts.reduce((t, g) => t + Math.max(g.campaigns.length, 1), 0)} contradict our findings.</strong></>}
                      </div>
                    </div>
                  </RailCard>
                )}
              </aside>
            </div>

            {/* Budget */}
            {(run.budget?.google || run.budget?.meta) && platform !== 'cross' && platform !== 'ga4' && (
              <section style={{ ...card, overflow: 'hidden' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: `1px solid ${T.line}` }}>
                  <div style={{ flex: '1 1 260px' }}>
                    <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Budget recommendations</h2>
                    <div style={{ fontSize: 13, color: T.ink3, marginTop: 2 }}>Daily budgets, at most ±30% per campaign per step. Apply weekly.</div>
                  </div>
                  <Segmented value={budgetTab} onChange={setBudgetTab} options={[
                    ...(run.budget?.google ? [{ value: 'google', label: 'Google Ads', icon: <GoogleAdsLogo size={14} /> }] : []),
                    ...(run.budget?.meta ? [{ value: 'meta', label: 'Meta', icon: <MetaLogo size={14} /> }] : []),
                  ]} />
                </div>
                {run.budget?.cross && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 24px', padding: '14px 20px', borderBottom: `1px solid ${T.line2}`, background: T.soft }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: T.ink2 }}>Channel mix</span>
                    <div style={{ flex: '1 1 240px', display: 'flex', height: 10, borderRadius: 999, overflow: 'hidden', background: T.line2 }}>
                      <div style={{ width: `${Math.max(0, Math.min(100, run.budget.cross.meta_share ?? 0))}%`, background: PLATFORM.meta.color }} />
                      <div style={{ flex: 1, background: '#34A853' }} />
                    </div>
                    <span style={{ fontSize: 13, color: T.ink2, fontVariantNumeric: 'tabular-nums' }}>
                      <strong style={{ color: PLATFORM.meta.color }}>Meta</strong> {pct(run.budget.cross.meta_share)} · {roasX(run.budget.cross.meta_roas)}
                      <span style={{ color: T.ink4, margin: '0 10px' }}>|</span>
                      <strong style={{ color: '#188038' }}>Google</strong> {pct(run.budget.cross.google_share)} · {roasX(run.budget.cross.google_roas)}
                    </span>
                    {run.budget.cross.verdict && <div style={{ flexBasis: '100%', fontSize: 13, color: T.ink, lineHeight: 1.5 }}>{run.budget.cross.verdict}</div>}
                  </div>
                )}
                <BudgetPanel key={budgetTab} data={budgetData} />
              </section>
            )}

            {/* Google's own recommendations */}
            {googleRecs.length > 0 && (platform === 'all' || platform === 'google') && (
              <section id="hc-google-recs" style={{ ...card, overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.line}` }}>
                  <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}><GoogleAdsLogo /> Google's own recommendations</h2>
                  <div style={{ fontSize: 13, color: T.ink3, marginTop: 4, lineHeight: 1.5 }}>As shown in the Google Ads interface. Recommendations that contradict our findings are flagged; review them before applying.</div>
                </div>
                {googleRecs.map((g, i) => (
                  <div key={g.key} style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 20px', alignItems: 'flex-start', padding: '12px 20px', borderTop: i ? `1px solid ${T.line2}` : 'none', background: g.conflict ? '#FFF7F7' : T.card, fontSize: 13 }}>
                    <div style={{ flex: '0 1 260px', minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: T.ink }}>{g.label}</div>
                      <div style={{ color: T.ink3, marginTop: 2 }}>{g.campaigns.length ? `${g.campaigns.length} campaign${g.campaigns.length > 1 ? 's' : ''}` : 'Account level'}</div>
                    </div>
                    <div style={{ flex: '1 1 320px', minWidth: 0, color: T.ink2, lineHeight: 1.6, overflowWrap: 'anywhere' }}>{g.campaigns.join(' · ')}</div>
                    {g.conflict && (
                      <button type="button" onClick={() => jumpTo(g.conflict)} style={{ ...unbutton, display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: '#B91C1C', background: T.card, border: '1px solid #FECACA', padding: '4px 10px', borderRadius: 999 }}>
                        <AlertTriangle size={12} /> Contradicts: {checks.find((x) => x.key === g.conflict)?.title || g.conflict}
                      </button>
                    )}
                  </div>
                ))}
              </section>
            )}

            </>)}

            {run.errors?.length > 0 && (
              <div style={{ fontSize: 12, color: T.ink3 }}>Sources that couldn't be read: {run.errors.map((e) => `${e.source} (${e.message})`).join(' · ')}</div>
            )}
            <div style={{ fontSize: 12, color: T.ink4, textAlign: 'center', lineHeight: 1.5 }}>
              This page only reads Google Ads, Meta, GA4 and the store data; it never changes campaigns, budgets or settings. Recommendations are applied by the account manager.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
